import { z } from 'zod/v4';
import { Brand } from '../branded-types';

// Color were taken from https://amconsoftware.sharepoint.com/:w:/r/sites/Entwicklung/_layouts/15/Doc.aspx?sourcedoc=%7B74F4E550-0011-4291-AEE2-054BEFA81F01%7D&file=Farben%20f%25u00fcr%20Webshop.docx&action=default&mobileredirect=true

export type ColorAdmixtureId = Brand<string, 'colorAdmixtureId'>;

export const COLOR_ADMIXTURE_SCHEMA: z.ZodType<IColorAdmixture> = z.object({
  id: z
    .uuid()
    .transform((s: string) => s as ColorAdmixtureId)
    .optional(),
  name: z.string(),
  baseColor: z.string(),
  mixColor: z.string(),
  mixColorPercentage: z.number(),
  primaryPercentage: z.number(),
});

export interface IColorAdmixture {
  id?: ColorAdmixtureId;
  name: string;
  baseColor: string;
  mixColor: string;
  mixColorPercentage: number;
  primaryPercentage: number;
}

export const STANDARD_BACKGROUND: IColorAdmixture = {
  id: 'd31c9469-d1ae-446d-8351-8384eafdb0b6' as ColorAdmixtureId,
  name: 'STANDARD_BACKGROUND',
  baseColor: '#FFFFFF',
  mixColor: '#000000',
  mixColorPercentage: 0,
  primaryPercentage: 0,
};

export const STANDARD_TEXT: IColorAdmixture = {
  id: 'c2a61e50-9b26-4b21-9010-7ff1aab607df' as ColorAdmixtureId,
  name: 'STANDARD_TEXT',
  baseColor: '#4B4E55',
  mixColor: '#000000',
  mixColorPercentage: 0,
  primaryPercentage: 0,
};

export const STANDARD_TEXT_HIGH: IColorAdmixture = {
  id: 'c3f27bd1-2e41-493a-865a-3a5c86b97530' as ColorAdmixtureId,
  name: 'STANDARD_TEXT_HIGH',
  baseColor: '#333333',
  mixColor: '#000000',
  mixColorPercentage: 0,
  primaryPercentage: 0,
};

export const STANDARD_TEXT_DISABLED: IColorAdmixture = {
  id: '823024f6-b446-49c6-8747-018dbb9229c2' as ColorAdmixtureId,
  name: 'STANDARD_TEXT_DISABLED',
  baseColor: '#7A7A7A',
  mixColor: '#000000',
  mixColorPercentage: 0,
  primaryPercentage: 0,
};

export const STANDARD_TEXT_OUTLINED: IColorAdmixture = {
  id: '6472a9a3-d0c3-4036-ae87-fbe2d164a1ee' as ColorAdmixtureId,
  name: 'STANDARD_TEXT_OUTLINED',
  baseColor: '#C4C4CF',
  mixColor: '#000000',
  mixColorPercentage: 0,
  primaryPercentage: 0,
};

export const STANDARD_TEXT_DIVIDER: IColorAdmixture = {
  id: 'a5b62a8e-43cb-4e26-9330-e8bb84afb3c6' as ColorAdmixtureId,
  name: 'STANDARD_TEXT_DIVIDER',
  baseColor: '#FFFFFF',
  mixColor: '#C4C4CF',
  mixColorPercentage: 0.3,
  primaryPercentage: 0,
};

export const ERROR_RED: IColorAdmixture = {
  id: 'b6a4c837-f400-4f3c-b4a9-52afd8436715' as ColorAdmixtureId,
  name: 'ERROR_RED',
  baseColor: '#BA1A1A',
  mixColor: '#000000',
  mixColorPercentage: 0,
  primaryPercentage: 0,
};

export const ERROR_RED_DARK: IColorAdmixture = {
  id: '13d52db8-4c3c-449e-b115-3c444db7a893' as ColorAdmixtureId,
  name: 'ERROR_RED_DARK',
  baseColor: '#BA1A1A',
  mixColor: '#FFFFFF',
  mixColorPercentage: 0.4,
  primaryPercentage: 0,
};

export const STANDARD_SUCCESS: IColorAdmixture = {
  id: 'bdd786e0-dc84-49e8-bc83-f65c7749836d' as ColorAdmixtureId,
  name: 'STANDARD_SUCCESS',
  baseColor: '#32C10E',
  mixColor: '#FFFFFF',
  mixColorPercentage: 0.4,
  primaryPercentage: 0,
};

export const DARK_BACKGROUND: IColorAdmixture = {
  id: '162bbdf3-5300-42d7-9bc4-57cba78887f4' as ColorAdmixtureId,
  name: 'DARK_BACKGROUND',
  baseColor: '#121212',
  mixColor: '#FFFFFF',
  mixColorPercentage: 0.0,
  primaryPercentage: 0.0,
};

export const DARK_SURFACE_01: IColorAdmixture = {
  id: 'fa84608a-06bd-44e6-966b-ee07a6bdf947' as ColorAdmixtureId,
  name: 'DARK_SURFACE_01',
  baseColor: '#121212',
  mixColor: '#FFFFFF',
  mixColorPercentage: 0.05,
  primaryPercentage: 0.01,
};

export const DARK_SURFACE_02: IColorAdmixture = {
  id: 'ec14a097-8d30-40da-ae5f-0be2a4589fa7' as ColorAdmixtureId,
  name: 'DARK_SURFACE_02',
  baseColor: '#121212',
  mixColor: '#FFFFFF',
  mixColorPercentage: 0.07,
  primaryPercentage: 0.01,
};

export const DARK_SURFACE_03: IColorAdmixture = {
  id: '21f5a6a9-9fbb-4679-b927-cc55089aa7df' as ColorAdmixtureId,
  name: 'DARK_SURFACE_03',
  baseColor: '#121212',
  mixColor: '#FFFFFF',
  mixColorPercentage: 0.08,
  primaryPercentage: 0.01,
};

export const DARK_SURFACE_04: IColorAdmixture = {
  id: 'b34eeab0-8405-421e-a48d-acaa72f4f59a' as ColorAdmixtureId,
  name: 'DARK_SURFACE_04',
  baseColor: '#121212',
  mixColor: '#FFFFFF',
  mixColorPercentage: 0.09,
  primaryPercentage: 0.01,
};

export const DARK_SURFACE_08: IColorAdmixture = {
  id: 'faffc7da-f831-4015-bc45-e40531d55fa7' as ColorAdmixtureId,
  name: 'DARK_SURFACE_08',
  baseColor: '#121212',
  mixColor: '#FFFFFF',
  mixColorPercentage: 0.12,
  primaryPercentage: 0.01,
};

export const DARK_SURFACE_END: IColorAdmixture = {
  id: 'ea42a78b-7777-4ed2-ba2a-e7a09ded9e6b' as ColorAdmixtureId,
  name: 'DARK_SURFACE_END',
  baseColor: '#121212',
  mixColor: '#FFFFFF',
  mixColorPercentage: 0.16,
  primaryPercentage: 0.01,
};

export const DARK_TEXT: IColorAdmixture = {
  id: '3603c0f8-c799-445a-b4c4-7b07bf82f191' as ColorAdmixtureId,
  name: 'DARK_TEXT',
  baseColor: '#000000',
  mixColor: '#FFFFFF',
  mixColorPercentage: 0.6,
  primaryPercentage: 0.03,
};

export const DARK_TEXT_HIGH: IColorAdmixture = {
  id: '12c4b257-2137-432c-ad00-ec7d9d2692db' as ColorAdmixtureId,
  name: 'DARK_TEXT_HIGH',
  baseColor: '#000000',
  mixColor: '#FFFFFF',
  mixColorPercentage: 0.87,
  primaryPercentage: 0.03,
};

export const DARK_TEXT_DISABLED: IColorAdmixture = {
  id: 'bbec1910-4d51-4c3e-8009-541c81fa4c6e' as ColorAdmixtureId,
  name: 'DARK_TEXT_DISABLED',
  baseColor: '#000000',
  mixColor: '#FFFFFF',
  mixColorPercentage: 0.38,
  primaryPercentage: 0.03,
};

export const DARK_PRIMARY: IColorAdmixture = {
  id: '6c12a5f7-3f17-4e96-95c7-c73c65b892f0' as ColorAdmixtureId,
  name: 'DARK_PRIMARY',
  baseColor: '#000000',
  mixColor: '#FFFFFF',
  mixColorPercentage: 0.2,
  primaryPercentage: 1,
};

export const DARK_PRIMARY_TEXT: IColorAdmixture = {
  id: '91283247-8e40-477e-92ec-890e025c53f2' as ColorAdmixtureId,
  name: 'DARK_PRIMARY_TEXT',
  baseColor: '#000000',
  mixColor: '#FFFFFF',
  mixColorPercentage: 0.97,
  primaryPercentage: 1.0,
};

export const DEFAULT_COLOR_ADMIXTURES: IColorAdmixture[] = [
  STANDARD_BACKGROUND,
  STANDARD_TEXT,
  STANDARD_TEXT_HIGH,
  STANDARD_TEXT_DISABLED,
  STANDARD_TEXT_OUTLINED,
  STANDARD_TEXT_DIVIDER,
  ERROR_RED,
  ERROR_RED_DARK,
  STANDARD_SUCCESS,
  DARK_BACKGROUND,
  DARK_SURFACE_01,
  DARK_SURFACE_02,
  DARK_SURFACE_03,
  DARK_SURFACE_04,
  DARK_SURFACE_08,
  DARK_SURFACE_END,
  DARK_TEXT,
  DARK_TEXT_HIGH,
  DARK_TEXT_DISABLED,
  DARK_PRIMARY,
  DARK_PRIMARY_TEXT,
];
